import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, AlertTriangle, Loader2, CheckCircle2, ChevronDown, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * Jun-2026 — Mitarbeiter-Auslastungsübersicht für den Händler.
 * Jul-2026 (User-Wunsch): Klick auf einen Mitarbeiter klappt dessen konkrete
 * Aufgaben auf (offen/überfällig/erledigt) mit Aktionen: erledigte bestätigen,
 * Aufgabe öffnen, und optional „beim Zuweisen vorschlagen".
 */
const fmtD = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }); } catch { return ""; } };
const fmtMin = (min) => {
    const h = Math.floor((min || 0) / 60); const m = (min || 0) % 60;
    return h > 0 ? `${h} Std ${m > 0 ? `${m} Min` : ""}`.trim() : `${m} Min`;
};

const SALES_FN = ["verkaeufer", "verkaufsleiter"];
const WORKSHOP_FN = ["kundendienst", "mechaniker"];

function DailyStats({ r }) {
    const fn = r.employee_function || "";
    const isSales = SALES_FN.includes(fn);
    const isWorkshop = WORKSHOP_FN.includes(fn);
    if (!isSales && !isWorkshop) return null;
    const hasLeads = (r.leads_today || 0) + (r.leads_red || 0) + (r.leads_yellow || 0) + (r.leads_green || 0) > 0;
    return (
        <div className="flex flex-wrap items-center gap-1.5 mt-1 ml-5" data-testid={`workload-daily-${r.assignee_id}`}>
            {isSales && (
                <>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700" data-testid={`workload-leads-today-${r.assignee_id}`}>
                        {r.leads_today || 0} Lead{(r.leads_today || 0) === 1 ? "" : "s"} heute bearbeitet
                    </span>
                    {hasLeads && (
                        <span className="inline-flex items-center gap-2 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-gray-50 text-gray-600" data-testid={`workload-ampel-${r.assignee_id}`} title="Ampel-Status der offenen Leads">
                            <span className="inline-flex items-center gap-0.5"><span className="w-2 h-2 rounded-full bg-red-500" /> {r.leads_red || 0}</span>
                            <span className="inline-flex items-center gap-0.5"><span className="w-2 h-2 rounded-full bg-amber-400" /> {r.leads_yellow || 0}</span>
                            <span className="inline-flex items-center gap-0.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> {r.leads_green || 0}</span>
                        </span>
                    )}
                </>
            )}
            {isWorkshop && (
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${(r.time_booked_min || 0) > 0 ? "bg-indigo-50 text-indigo-700" : "bg-gray-50 text-gray-400"}`} data-testid={`workload-time-${r.assignee_id}`} title="Heute auf Werkstattaufträge gebuchte Arbeitszeit">
                    {fmtMin(r.time_booked_min)} auf Aufträge gebucht{r.time_running ? " · Timer läuft" : ""}
                </span>
            )}
            {fn === "kundendienst" && (
                <>
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${(r.orders_planned_today || 0) > 0 ? "bg-sky-50 text-sky-700" : "bg-gray-50 text-gray-400"}`} data-testid={`workload-planned-${r.assignee_id}`} title="Heute eingeplante Werkstattaufträge (Termin heute)">
                    {r.orders_planned_today || 0} heute eingeplant
                </span>
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${(r.orders_today || 0) > 0 ? "bg-emerald-50 text-emerald-700" : "bg-gray-50 text-gray-400"}`} data-testid={`workload-orders-${r.assignee_id}`} title="Heute angenommene Werkstattaufträge">
                    {r.orders_today || 0} Auftr{(r.orders_today || 0) === 1 ? "ag" : "äge"} heute angenommen
                </span>
                </>
            )}
        </div>
    );
}

const ABS_LABEL = { urlaub: "Urlaub", schule: "Schule", ausgleich: "Ausgleich", krank: "Krank", feiertag: "Feiertag" };
const hm = (iso) => (iso ? new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "–");

// Anwesenheit heute — links neben den Aufgaben-Chips (User-Wunsch Jun-2026).
function PresenceChip({ t }) {
    if (!t) return null;
    const tip = `Ankunft ${hm(t.arrival)} · Heute ${t.today_hours > 0 ? `${t.today_hours} h` : "–"} · Pause ${t.today_pause_minutes ? `${t.today_pause_minutes}m` : "–"}${t.krank_days ? ` · Krank ${t.krank_days} Tag(e)` : ""}`;
    if (t.absence) {
        return (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-sm bg-amber-50 text-amber-700" title={tip} data-testid={`workload-presence-${t.employee_id}`}>
                {ABS_LABEL[t.absence] || t.absence}
            </span>
        );
    }
    if (t.clocked_in) {
        return (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700" title={tip} data-testid={`workload-presence-${t.employee_id}`}>
                <span className="w-2 h-2 rounded-full bg-emerald-500" /> {hm(t.arrival)}{t.today_hours > 0 ? ` · ${t.today_hours} h` : ""}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-sm bg-gray-50 text-gray-400" title={tip} data-testid={`workload-presence-${t.employee_id}`}>
            <span className="w-2 h-2 rounded-full bg-gray-300" /> {t.today_hours > 0 ? `${t.today_hours} h` : "abwesend"}
        </span>
    );
}

export default function TeamWorkloadCard() {
    const [rows, setRows] = useState(null);
    const [tasks, setTasks] = useState(null); // alle delegated_tasks des Händlers
    const [timeItems, setTimeItems] = useState([]); // Anwesenheit heute (zusammengeführt)
    const [expanded, setExpanded] = useState(null); // assignee_id
    const navigate = useNavigate();

    const load = () => {
        api.get("/dealer/tasks/workload").then((r) => setRows(r.data.items || [])).catch(() => setRows([]));
        api.get("/dealer/tasks").then((r) => setTasks(r.data.items || [])).catch(() => setTasks([]));
        api.get("/dealer/time/overview").then((r) => setTimeItems(r.data?.items || [])).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    const quickAssign = (r, e) => {
        e.stopPropagation();
        try { localStorage.setItem("tw_quick_assign_assignee", r.assignee_id); } catch { /* ignore */ }
        toast.message(`„${r.assignee_name}" wird beim Zuweisen vorgeschlagen`, {
            description: "Wähle in „Was zu tun ist“ eine Aufgabe und klicke „Zuweisen“.",
        });
        navigate("/dashboard?tab=inbox");
    };

    const ackTask = async (t) => {
        try {
            await api.post(`/dealer/tasks/${t.id}/ack`);
            toast.success("Zur Kenntnis genommen");
            load();
        } catch { toast.error("Aktion fehlgeschlagen"); }
    };

    if (rows === null) {
        return <div className="rounded-sm border border-gray-200 p-4 text-sm text-gray-400 flex items-center gap-2" data-testid="team-workload-loading"><Loader2 className="w-4 h-4 animate-spin" /> Lade Auslastung…</div>;
    }
    const visible = rows.filter((r) => (r.open + r.overdue + r.done_unack) > 0
        || (r.leads_today || 0) > 0 || (r.leads_red || 0) > 0 || (r.leads_yellow || 0) > 0 || (r.leads_green || 0) > 0
        || (r.time_booked_min || 0) > 0 || (r.orders_today || 0) > 0);
    // Zusammengeführt (User-Wunsch): auch Mitarbeiter ohne Aufgaben aus der
    // Anwesenheit anzeigen, damit eine Karte alles abbildet.
    const timeBy = {};
    timeItems.forEach((t) => { timeBy[t.employee_id] = t; if (t.name) timeBy[`n:${t.name}`] = t; });
    const timeFor = (r) => timeBy[r.assignee_id] || timeBy[`n:${r.assignee_name}`] || null;
    const known = new Set(visible.map((r) => r.assignee_id).concat(visible.map((r) => r.assignee_name)));
    const extraRows = timeItems
        .filter((t) => !known.has(t.employee_id) && !known.has(t.name))
        .map((t) => ({ assignee_id: t.employee_id, assignee_name: t.name, employee_function: t.employee_function, open: 0, overdue: 0, done_unack: 0 }));
    const allRows = [...visible, ...extraRows];
    if (allRows.length === 0) return null;

    const tasksOf = (aid) => (tasks || [])
        .filter((t) => t.assignee_id === aid && (t.status === "open" || (t.status === "done" && !t.dealer_ack)))
        .sort((a, b) => (a.status === b.status ? (b.overdue ? 1 : 0) - (a.overdue ? 1 : 0) : a.status === "open" ? -1 : 1))
        .slice(0, 10);

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="team-workload-card">
            <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-[#111827]" />
                <h3 className="font-display font-black text-base">Mitarbeiter-Auslastung & Anwesenheit</h3>
            </div>
            <p className="text-[11px] text-gray-400 mb-3">Klick auf einen Mitarbeiter zeigt seine Aufgaben — erledigte kannst du dort bestätigen. Details zur Anwesenheit per Maus über den Status.</p>
            <ul className="space-y-1.5">
                {allRows.map((r) => (
                    <li key={r.assignee_id}>
                        <button
                            type="button"
                            onClick={() => setExpanded(expanded === r.assignee_id ? null : r.assignee_id)}
                            className="w-full flex items-center justify-between gap-3 border border-gray-100 rounded-sm px-3 py-2 text-left hover:border-[#FFCC00] hover:bg-[#FFCC00]/5 transition-colors"
                            data-testid={`workload-${r.assignee_id}`}
                            title="Aufgaben dieses Mitarbeiters anzeigen"
                        >
                            <span className="flex items-center gap-1.5 font-bold text-sm text-gray-900 truncate">
                                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform ${expanded === r.assignee_id ? "" : "-rotate-90"}`} />
                                {r.assignee_name}
                                {r.absent && <span className="text-[10px] font-bold bg-amber-100 text-amber-800 rounded-sm px-1.5 py-0.5">{r.absent_label || "abwesend"}</span>}
                            </span>
                            <div className="flex items-center gap-2 shrink-0 font-mono-num">
                                <PresenceChip t={timeFor(r)} />
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-sm bg-gray-100 text-gray-700" data-testid={`workload-open-${r.assignee_id}`}>
                                    {r.open} offen
                                </span>
                                <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-sm ${r.overdue > 0 ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-400"}`} data-testid={`workload-overdue-${r.assignee_id}`}>
                                    <AlertTriangle className="w-3 h-3" /> {r.overdue} überfällig
                                </span>
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700" data-testid={`workload-done-${r.assignee_id}`}>
                                    <CheckCircle2 className="w-3 h-3" /> {r.done_unack} erledigt
                                </span>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => quickAssign(r, e)}
                                    onKeyDown={(e) => e.key === "Enter" && quickAssign(r, e)}
                                    className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-sm border border-gray-200 text-gray-500 hover:border-[#111827] hover:text-[#111827]"
                                    title="Diesen Mitarbeiter beim nächsten „Zuweisen“ in „Was zu tun ist“ vorschlagen"
                                    data-testid={`workload-quick-assign-${r.assignee_id}`}
                                >
                                    <UserPlus className="w-3 h-3" /> Zuweisen
                                </span>
                            </div>
                        </button>
                        <DailyStats r={r} />
                        {expanded === r.assignee_id && (
                            <div className="ml-5 mt-1 border-l-2 border-gray-100 pl-3 space-y-1" data-testid={`workload-tasks-${r.assignee_id}`}>
                                {tasks === null ? (
                                    <div className="text-xs text-gray-400 flex items-center gap-1.5 py-1"><Loader2 className="w-3 h-3 animate-spin" /> Lade Aufgaben…</div>
                                ) : tasksOf(r.assignee_id).length === 0 ? (
                                    <div className="text-xs text-gray-400 py-1">Keine offenen Aufgaben.</div>
                                ) : tasksOf(r.assignee_id).map((t) => (
                                    <div key={t.id} className="flex items-center gap-2 py-1 text-xs" data-testid={`workload-task-${t.id}`}>
                                        {t.status === "done"
                                            ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                            : t.overdue
                                                ? <AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0" />
                                                : <span className="w-3.5 h-3.5 shrink-0 flex items-center justify-center"><span className="w-2 h-2 rounded-full bg-gray-300" /></span>}
                                        <button type="button"
                                            onClick={() => t.cta_url && navigate(t.cta_url)}
                                            className={`flex-1 min-w-0 truncate text-left ${t.cta_url ? "hover:underline" : "cursor-default"} ${t.overdue ? "text-red-700 font-bold" : "text-gray-700"}`}
                                            title={t.title}>
                                            {t.title}
                                        </button>
                                        {t.due_at && t.status === "open" && (
                                            <span className={`shrink-0 font-mono-num ${t.overdue ? "text-red-600 font-bold" : "text-gray-400"}`}>fällig {fmtD(t.due_at)}</span>
                                        )}
                                        {t.status === "done" && !t.dealer_ack && (
                                            <button type="button" onClick={() => ackTask(t)}
                                                className="shrink-0 text-[10px] font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm px-2 py-0.5"
                                                title="Erledigte Aufgabe zur Kenntnis nehmen (verschwindet aus der Liste)"
                                                data-testid={`workload-ack-${t.id}`}>
                                                OK
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}
